import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'today';
  if (date.toDateString() === yesterday.toDateString()) return 'yesterday';

  return date.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function formatFullDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString([], {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function renderText(text) {
  if (!text) return null;
  return text.split('\n').map((line, i, arr) => {
    const parts = line.split(/(\*[^*]+\*)/g);
    return (
      <span key={i}>
        {parts.map((part, j) => {
          if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
            return <em key={j}>{part.slice(1, -1)}</em>;
          }
          return <span key={j}>{part}</span>;
        })}
        {i < arr.length - 1 && <br />}
      </span>
    );
  });
}

function Journal() {
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [companion, setCompanion] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [rerolling, setRerolling] = useState(false);
  const [toast, setToast] = useState('');
  const [versions, setVersions] = useState([]);
  const [versionIndex, setVersionIndex] = useState(0);
  const { companionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCompanion();
    fetchEntries();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const fetchCompanion = async () => {
    try {
      const res = await axios.get(`${API}/companions?token=${token}`);
      const found = res.data.companions.find(c => c.id === companionId);
      setCompanion(found);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/journal/${companionId}?token=${token}`);
      setEntries(res.data.entries || []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchVersions = async (date) => {
    try {
      const res = await axios.get(`${API}/journal/${companionId}/versions/${date}?token=${token}`);
      setVersions(res.data.versions || []);
      setVersionIndex(0);
    } catch (err) {
      console.log(err);
      setVersions([]);
      setVersionIndex(0);
    }
  };

  const openEntry = (entry) => {
    setSelected(entry.id);
    fetchVersions(entry.date);
  };

  const generateAll = async () => {
    setGenerating(true);
    showToast('writing… ✦');
    try {
      const res = await axios.post(`${API}/journal/generate/${companionId}?token=${token}`);
      const { generated, skipped, entries: newEntries } = res.data;
      if (generated > 0) {
        await fetchEntries();
        showToast(`${generated} ${generated === 1 ? 'entry' : 'entries'} written ♡`);
      } else {
        showToast('all caught up ♡');
      }
    } catch (err) {
      console.log(err);
      showToast('something went wrong 🥀');
    }
    setGenerating(false);
  };

  const rerollEntry = async (entry) => {
    setRerolling(true);
    showToast('rewriting… ↺');
    try {
      const res = await axios.post(`${API}/journal/reroll/${entry.id}?token=${token}`);
      await fetchEntries();
      await fetchVersions(entry.date);
      showToast('rewritten ♡');
    } catch (err) {
      console.log(err);
      showToast('something went wrong 🥀');
    }
    setRerolling(false);
  };

  const selectedEntry = selected !== null ? entries.find(e => e.id === selected) : null;
  const currentVersion = versions[versionIndex] || selectedEntry;
  const hasOlder = versionIndex < versions.length - 1;
  const hasNewer = versionIndex > 0;

  if (selectedEntry && currentVersion) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backBtn} onClick={() => setSelected(null)}>←</button>
          <div style={styles.headerTitleWrap}>
            <div style={styles.headerDate}>{formatDisplayDate(selectedEntry.date)}</div>
            <div style={styles.headerSub}>{formatFullDate(selectedEntry.date)}</div>
          </div>
          <button
            style={{ ...styles.rerollBtn, opacity: rerolling ? 0.5 : 1 }}
            onClick={() => rerollEntry(selectedEntry)}
            disabled={rerolling}
            title="rewrite this entry"
          >
            ↺
          </button>
        </div>

        {versions.length > 1 && (
          <div style={styles.versionNav}>
            <button
              style={{ ...styles.versionNavBtn, opacity: hasOlder ? 1 : 0.3 }}
              onClick={() => hasOlder && setVersionIndex(i => i + 1)}
              disabled={!hasOlder}
            >
              ‹
            </button>
            <div style={styles.versionNavLabel}>
              version {versions.length - versionIndex} of {versions.length}
            </div>
            <button
              style={{ ...styles.versionNavBtn, opacity: hasNewer ? 1 : 0.3 }}
              onClick={() => hasNewer && setVersionIndex(i => i - 1)}
              disabled={!hasNewer}
            >
              ›
            </button>
          </div>
        )}

        <div style={styles.entryContent}>
          <div style={styles.entryBody}>
            <div style={styles.quoteAccent}>"</div>
            {currentVersion.content.split('\n\n').map((para, i) => (
              para.trim() ? <p key={i} style={styles.entryParagraph}>{renderText(para)}</p> : null
            ))}
          </div>
        </div>

        {toast && <div style={styles.toast}>{toast}</div>}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate(`/memory/${companionId}`)}>←</button>
        <div style={styles.headerTitleWrap}>
          <div style={styles.headerTitle}>
            <span style={styles.headerTitleItalic}>his</span> journal
          </div>
          <div style={styles.headerSub}>written from his perspective ♡</div>
        </div>
        <button
          style={{ ...styles.generateBtn, opacity: generating ? 0.5 : 1 }}
          onClick={generateAll}
          disabled={generating}
          title="generate missing entries"
        >
          {generating ? '…' : '✦'}
        </button>
      </div>

      <div style={styles.content}>
        {entries.length === 0 && !generating && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>♡</div>
            <div style={styles.emptyTitle}>no entries yet</div>
            <div style={styles.emptyHint}>tap ✦ above to write all entries</div>
          </div>
        )}

        {generating && entries.length === 0 && (
          <div style={styles.emptyState}>
            <div style={styles.emptyTitle}>writing… ✦</div>
            <div style={styles.emptyHint}>this might take a moment</div>
          </div>
        )}

        {entries.map((entry) => (
          <button
            key={entry.id}
            style={styles.entryCard}
            onClick={() => openEntry(entry)}
          >
            <div style={styles.entryCardLeft}>
              <div style={styles.entryCardDate}>{formatDisplayDate(entry.date)}</div>
              <div style={styles.entryCardFull}>{formatFullDate(entry.date)}</div>
              <div style={styles.entryCardPreview}>
                {entry.content.slice(0, 120).trim()}
                {entry.content.length > 120 ? '…' : ''}
              </div>
            </div>
            <div style={styles.entryCardArrow}>›</div>
          </button>
        ))}
      </div>

      {toast && <div style={styles.toast}>{toast}</div>}
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: '#0e0b0f',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: '24px 28px 20px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
    borderBottom: '1px solid rgba(201,147,90,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  backBtn: {
    width: '36px', height: '36px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#c9935a', fontSize: '16px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  headerTitleWrap: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  headerTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '22px', color: '#e8ddd0',
    fontWeight: '300',
  },
  headerTitleItalic: { fontStyle: 'italic' },
  headerDate: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '20px', color: '#e8ddd0',
    fontWeight: '300', fontStyle: 'italic',
  },
  headerSub: {
    fontSize: '11px', color: '#4a3d30',
    fontWeight: '300', marginTop: '2px',
    textAlign: 'center',
  },
  generateBtn: {
    width: '36px', height: '36px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.25)',
    background: 'rgba(201,147,90,0.08)',
    color: '#c9935a', fontSize: '14px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  rerollBtn: {
    width: '36px', height: '36px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.25)',
    background: 'rgba(201,147,90,0.08)',
    color: '#c9935a', fontSize: '18px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  content: {
    flex: 1, overflowY: 'auto',
    padding: '24px 28px 80px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  emptyState: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    flex: 1, gap: '12px', paddingTop: '80px',
  },
  emptyIcon: { fontSize: '32px', color: '#2a1f16' },
  emptyTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '22px', color: '#4a3d30',
    fontWeight: '300', fontStyle: 'italic',
  },
  emptyHint: { fontSize: '12px', color: '#2a1f16', fontWeight: '300' },
  entryCard: {
    background: '#1e1825', borderRadius: '20px',
    border: '1px solid rgba(201,147,90,0.08)',
    padding: '20px 20px 20px 22px',
    display: 'flex', alignItems: 'center', gap: '12px',
    cursor: 'pointer', textAlign: 'left', width: '100%',
  },
  entryCardLeft: {
    flex: 1, display: 'flex', flexDirection: 'column', gap: '4px',
  },
  entryCardDate: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '18px', color: '#e8ddd0',
    fontWeight: '300', fontStyle: 'italic', textTransform: 'lowercase',
  },
  entryCardFull: {
    fontSize: '11px', color: '#4a3d30',
    fontWeight: '300', marginBottom: '6px',
  },
  entryCardPreview: {
    fontSize: '13px', color: '#7a6a5a',
    fontWeight: '300', lineHeight: '1.6',
  },
  entryCardArrow: { fontSize: '20px', color: '#3a2a1a', flexShrink: 0 },
  entryContent: {
    flex: 1, overflowY: 'auto',
    padding: '32px 28px 80px',
  },
  versionNav: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '16px', padding: '12px 28px 0',
  },
  versionNavBtn: {
    width: '32px', height: '32px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#c9935a', fontSize: '16px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  versionNavLabel: {
    fontSize: '11px', color: '#4a3d30', fontWeight: '300',
    letterSpacing: '0.04em',
  },
  entryBody: { position: 'relative', paddingTop: '8px' },
  quoteAccent: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '120px', color: 'rgba(201,147,90,0.06)',
    position: 'absolute', top: '-40px', left: '-12px',
    lineHeight: 1, pointerEvents: 'none', userSelect: 'none',
  },
  entryParagraph: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '17px', color: '#c8bfb0',
    fontWeight: '300', lineHeight: '1.85',
    marginBottom: '24px', position: 'relative', zIndex: 1,
  },
  toast: {
    position: 'fixed', bottom: '32px', left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(201,147,90,0.15)',
    border: '1px solid rgba(201,147,90,0.3)',
    color: '#c9935a', padding: '10px 24px',
    borderRadius: '20px', fontSize: '13px',
    whiteSpace: 'nowrap', zIndex: 100,
  },
};

export default Journal;
